/* eslint-disable-next-line import/no-unresolved */
import { useCallback, useEffect, useRef, useState } from 'react';
/* eslint-disable-next-line import/no-unresolved */
import { useDispatch, useSelector } from 'react-redux';
import { doneRequestSequentialModal, setStatusSequentialModal } from '../redux/actions';
import { SEQUENTIAL_MODALS, STATUS } from '../constants/sequentialModals';

/**
 * 順番にモーダルを出すための制御用hookです
 * args
 *   isHome: ホーム画面にいる場合true, recommendを出すかどうかの判断
 *   isEnabled: 主にWeb用 外部Windowで起動されている時などにモーダルを出さないようにする
 * response:
 *    remainingModalCount
 *    canOpenAgreementAcceptModal
 *    canOpenTutorial
 *    canOpenAdvertisements
 *    canOpenMessageModal
 *    canOpenRecommend
 *    deactivateSequentialModals: 主にモバイル用 アプリがinactiveになった時など当処理を一時的に止める
 *    activateSequentialModals: 主にモバイル用 アプリがactiveになった時に処理を再開する
 */
const useSequentialModalController = ({ isHome, isEnabled = true }) => {
  const dispatch = useDispatch();
  const isAuth = useSelector((state) => state.auth.isAuthenticated);
  const isFirstLogin = useSelector((state) => state.settings.isFirstLogin) || false;
  const [savedIsFirstLogin, saveIsFirstLogin] = useState(null);
  const isInitializing = useSelector((state) => state.event.isInitializing);
  const {
    ready,
    status,
    remainingModalCount,
    nextAvailableTime: _,
    ...modals
  } = useSelector((state) => state.sequentialModals);
  const savedStatusRef = useRef(status);
  const isReadyBase = Boolean(isAuth && isEnabled && ready && !isInitializing);
  const isReady = Boolean(isReadyBase && status === STATUS.PROCESSING);
  const isReadyForRecommend = Boolean(
    isReadyBase && isHome && (status === STATUS.PROCESSING || status === STATUS.ALL_DONE),
  );

  const isDone = (modalId) => modals[modalId];

  const startProc = isReadyBase && status === STATUS.INIT;
  useEffect(() => {
    if (startProc) {
      dispatch(setStatusSequentialModal({ status: STATUS.PROCESSING }));
      saveIsFirstLogin(null);
    }
  }, [dispatch, startProc]);

  const allDone = (modals && Object.values(modals).every((done) => done)) || remainingModalCount === 0;
  const isDoneAgreementInfo = isDone(SEQUENTIAL_MODALS.AgreementInfo);
  const isDoneTutorialModal = isDone(SEQUENTIAL_MODALS.TutorialModal);
  const isDoneRecommend = isDone(SEQUENTIAL_MODALS.Recommend);
  const isDoneMessageModal = isDone(SEQUENTIAL_MODALS.MessageModal);
  const isDoneAdvertisements = isDone(SEQUENTIAL_MODALS.Advertisements);

  const endProc = allDone && status === STATUS.PROCESSING;
  useEffect(() => {
    if (endProc) {
      dispatch(setStatusSequentialModal({ status: STATUS.ALL_DONE }));
    }
  }, [dispatch, endProc]);

  const deactivateSequentialModals = useCallback(() => {
    if (status !== STATUS.ALL_DONE && status !== STATUS.NOT_READY) {
      dispatch(setStatusSequentialModal({ status: STATUS.NOT_READY }));
      savedStatusRef.current = status;
    }
  }, [dispatch, status]);
  const activateSequentialModals = useCallback(() => {
    if (savedStatusRef.current) {
      dispatch(setStatusSequentialModal({ status: savedStatusRef.current }));
      savedStatusRef.current = null;
    }
  }, [dispatch]);

  const { isLoading: isLoadingAdvertisements } = useSelector((state) => state.advertisement);
  const recommendData = useSelector((state) => state.recommend.recommendData);
  const hasRecommendData = !!recommendData;

  const noNeedToReadDisclaimer = (account) => !account || account.disclaimerReadFlg;
  const isAgreementAccept = useSelector((state) => {
    return Object.values(state.settings.accountInfo).every((s) => noNeedToReadDisclaimer(s));
  });

  const isOpenAgreementInfo = useSelector((state) => state.modals.agreementInfo.isOpen);
  useEffect(() => {
    if (isReady && isAgreementAccept && !isOpenAgreementInfo) {
      dispatch(doneRequestSequentialModal({ modalId: SEQUENTIAL_MODALS.AgreementInfo }));
    }
  }, [isAgreementAccept, dispatch, isReady, isOpenAgreementInfo]);

  useEffect(() => {
    if (isDoneTutorialModal) {
      if (savedIsFirstLogin) {
        // モーダルが閉じらたらfalseにする
        saveIsFirstLogin(false);
      }
      return; // 処理済みの場合終了
    }
    if (isReady && isDoneAgreementInfo) {
      // isFirstLoginは約款同意で更新されるため、initializeのタイミングと被るとチュートリアルを表示できない
      // そのため、約款同意されたときのisFirstLoginを保持しておく
      if (savedIsFirstLogin == null) {
        saveIsFirstLogin(isFirstLogin);
      } else if (savedIsFirstLogin === false) {
        dispatch(doneRequestSequentialModal({ modalId: SEQUENTIAL_MODALS.TutorialModal }));
      }
    }
  }, [isFirstLogin, savedIsFirstLogin, isDoneAgreementInfo, isDoneTutorialModal, dispatch, isReady]);

  useEffect(() => {
    if (isReady && isDoneTutorialModal && (!isHome || !hasRecommendData)) {
      dispatch(doneRequestSequentialModal({ modalId: SEQUENTIAL_MODALS.Recommend }));
    }
  }, [dispatch, isReady, isHome, hasRecommendData, isDoneTutorialModal]);

  const canOpenAgreementAcceptModal = isReady && !isAgreementAccept && !isDoneAgreementInfo;
  const canOpenTutorial = isReady && !!savedIsFirstLogin && isAgreementAccept && !isDoneTutorialModal;
  const canOpenRecommend = isReadyForRecommend && hasRecommendData && isDoneTutorialModal;
  const canOpenMessageModal =
    isReady && isDoneRecommend && !isDoneMessageModal && isDoneAgreementInfo && isDoneTutorialModal;
  const canOpenAdvertisements =
    isReady &&
    !isLoadingAdvertisements &&
    !isDoneAdvertisements &&
    isDoneMessageModal &&
    isDoneAgreementInfo &&
    isDoneTutorialModal;

  return {
    remainingModalCount,
    canOpenAgreementAcceptModal,
    canOpenTutorial,
    canOpenAdvertisements,
    canOpenMessageModal,
    canOpenRecommend,
    deactivateSequentialModals,
    activateSequentialModals,
    isSequentialModalComplete: status === STATUS.ALL_DONE,
  };
};

export default useSequentialModalController;
